import Http from '@/services/http';
import translate, { formatNumber } from '@/services/translate';
import { grammaticalJoin } from '@/services/arrayUtils';
import {
  CommutatorMaintenanceStatusResponse,
  CommutatorResponse,
  CommutatorDetailsResponse,
  CommutatorValues,
  DefaultCommutatorMaintenanceParameters,
  MaintenanceActionResponse,
  InsertMaintenanceActionRequest,
  MaintenanceActionByHistoryResponse,
  DeleteMaintenanceActionRequest,
  MaintenanceActionListPagedRequest,
  MaintenanceActionListPagedResponse,
  LastCommutationsResponse,
  CommutatorMaintenanceParameters,
} from 'sigma-mappings';
import serviceExceptions
  from '@/domain/data/installation/engineering/commutatorMaintenance/serviceExceptions';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';

const urls = {
  getStatus: assetModuleId => `/engenharia/${assetModuleId}/manutencao-comutador/status`,
  getCommutator: assetModuleId => `/engenharia/${assetModuleId}/manutencao-comutador/comutadores`,
  getCommutatorById: (assetModuleId, commutatorId) => `/engenharia/${assetModuleId}/manutencao-comutador/comutador/${commutatorId}`,
  getCommutatorValues: (assetModuleId, commutatorId) => `/engenharia/${assetModuleId}/manutencao-comutador/comutador/${commutatorId}/valores`,
  setCommutatorValues: (assetModuleId, commutatorId) => `/engenharia/${assetModuleId}/manutencao-comutador/comutador/${commutatorId}/valores`,
  getCommutatorMaintenanceAction: (assetModuleId, commutatorId) => `/engenharia/${assetModuleId}/manutencao-comutador/comutador/${commutatorId}/acao-manutencao`,
  setCommutatorMaintenanceAction: (assetModuleId, commutatorId) => `/engenharia/${assetModuleId}/manutencao-comutador/comutador/${commutatorId}/acao-manutencao`,
  getCommutatorActionById: (assetModuleId, commutatorId, maintenanceActionId) => `/engenharia/${assetModuleId}/manutencao-comutador/comutador/${commutatorId}/acao-manutencao/${maintenanceActionId}`,
  deleteCommutatorActionById: (assetModuleId, commutatorId, maintenanceActionId) => `/engenharia/${assetModuleId}/manutencao-comutador/comutador/${commutatorId}/acao-manutencao/${maintenanceActionId}`,
  getCommutatorActionsList: (assetModuleId, commutatorId) => `/engenharia/${assetModuleId}/manutencao-comutador/comutador/${commutatorId}/acoes-manutencao`,
  getLastCommutations: assetModuleId => `/engenharia/${assetModuleId}/manutencao-comutador/ultimas-comutacoes`,
  getParameters: assetModuleId => `/engenharia/${assetModuleId}/manutencao-comutador/parametro`,
  setParameters: assetModuleId => `/engenharia/${assetModuleId}/manutencao-comutador/parametro`,
  setDefaultParameters: assetModuleId => `/engenharia/${assetModuleId}/manutencao-comutador/parametro/default`,
};


/* istanbul ignore next */
const variablesError = {
  errorHandling: {
    ignore: error => (error.response?.data?.codigoMensagem || '') === 'MSIS176',
    responseMessageParams: {
      $MA029: (data) => {
        if (!data && !data.parameters) return null;
        const returnedField = serviceExceptions[data.parameters[2]];
        return {
          FaixaInicial: formatNumber(data.parameters[0]),
          FaixaFinal: formatNumber(data.parameters[1]),
          NomeDoCampo: translate(returnedField.label),
        };
      },
      $MA037: (data) => {
        if (!data && !data.parameters) return null;
        return data.parameters;
      },
      $MA078: (data) => {
        const parameters = Object.entries(data.parameters);
        const commutator = parameters[0][1];
        const variablesArray = parameters
          .slice(1, parameters.length)
          .map(item => item[1]);
        const variables = grammaticalJoin(variablesArray);
        return {
          variables,
          commutator,
        };
      },
      $MA085: data => ({
        variable: data.parameters[0],
      }),
    },
  },
};

/* istanbul ignore next */
const insertMaintenanceActionError = {
  errorHandling: {
    responseMessageParams: {
      $MA029: variablesError.errorHandling.responseMessageParams.$MA029,
      $MA038: (data) => {
        if (!data && !data.parameters) return null;

        const returnedField = serviceExceptions[data.parameters[1]];
        return {
          NomeDoCampo: translate(returnedField.label),
          FaixaInicial: dateTimeFormatter(data.parameters[0], formats.DATE_SHORT),
        };
      },
      $MA037: variablesError.errorHandling.responseMessageParams.$MA037,
      $MA039: (data) => {
        if (!data && !data.parameters) return null;

        const returnedField = serviceExceptions[data.parameters[0]];
        return {
          field: translate(returnedField.label),
        };
      },
      $MA040: (data) => {
        if (!data && !data.parameters) return null;

        const returnedField = serviceExceptions[data.parameters[0]];
        return {
          field: translate(returnedField.label),
          value: dateTimeFormatter(data.parameters[1], formats.DATE_SHORT),
        };
      },
      $ME057: (data) => {
        if (!data && !data.parameters) return null;
        return {
          date: data.parameters[0],
          commutator: data.parameters[1],
        };
      },
      $ME058: (data) => {
        if (!data && !data.parameters) return null;
        return {
          date: data.parameters[0],
          commutator: data.parameters[1],
          position: data.parameters[2],
        };
      },
      $MA078: variablesError.errorHandling.responseMessageParams.$MA078,
    },
  },
};

export default {
  getStatus(assetModuleId) {
    const http = new Http(variablesError);

    return http.get(urls.getStatus(assetModuleId))
      .then(({ data }) => new CommutatorMaintenanceStatusResponse(data));
  },
  getCommutator(assetModuleId) {
    const http = new Http(variablesError);

    return http.get(urls.getCommutator(assetModuleId))
      .then(({ data }) => data.map(item => new CommutatorResponse(item)));
  },
  getCommutatorById(assetModuleId, commutatorId) {
    const http = new Http(variablesError);

    return http.get(urls.getCommutatorById(assetModuleId, commutatorId))
      .then(({ data }) => new CommutatorDetailsResponse(data));
  },
  getCommutatorValues(assetModuleId, commutatorId) {
    const http = new Http(variablesError);

    return http.get(urls.getCommutatorValues(assetModuleId, commutatorId))
      .then(({ data }) => new CommutatorValues(data));
  },
  setCommutatorValues(assetModuleId, commutatorId, payload) {
    const http = new Http(variablesError);
    const mappedPayload = new CommutatorValues.MapBack(payload);
    return http.post(urls.setCommutatorValues(assetModuleId, commutatorId), mappedPayload);
  },
  getCommutatorMaintenanceAction(assetModuleId, commutatorId) {
    const http = new Http(variablesError);

    return http.get(urls.getCommutatorMaintenanceAction(assetModuleId, commutatorId))
      .then(({ data }) => new MaintenanceActionResponse(data));
  },
  setCommutatorMaintenanceAction(assetModuleId, commutatorId, payload) {
    const http = new Http(insertMaintenanceActionError);
    const mappedPayload = new InsertMaintenanceActionRequest(payload);
    return http
      .post(urls.setCommutatorMaintenanceAction(assetModuleId, commutatorId), mappedPayload);
  },
  getCommutatorActionById(assetModuleId, commutatorId, maintenanceActionId) {
    const http = new Http(variablesError);

    return http.get(urls.getCommutatorActionById(assetModuleId, commutatorId, maintenanceActionId))
      .then(({ data }) => new MaintenanceActionByHistoryResponse(data));
  },
  deleteCommutatorActionById(assetModuleId, commutatorId, maintenanceActionId, payload) {
    const http = new Http(variablesError);

    const mappedPayload = new DeleteMaintenanceActionRequest(payload);
    return http.delete(
      urls.deleteCommutatorActionById(assetModuleId, commutatorId, maintenanceActionId),
      { data: mappedPayload },
    );
  },
  getCommutatorActionsList(assetModuleId, commutatorId, payload) {
    const http = new Http(variablesError);

    const mappedPayload = new MaintenanceActionListPagedRequest(payload);
    return http.post(urls.getCommutatorActionsList(assetModuleId, commutatorId), mappedPayload)
      .then(({ data }) => new MaintenanceActionListPagedResponse(data));
  },
  getLastCommutations(assetModuleId) {
    const http = new Http(variablesError);

    return http.get(urls.getLastCommutations(assetModuleId))
      .then(({ data }) => data.map(item => new LastCommutationsResponse(item)));
  },
  getParameters(assetModuleId) {
    const http = new Http(variablesError);

    return http.get(urls.getParameters(assetModuleId))
      .then(({ data }) => new CommutatorMaintenanceParameters(data));
  },
  setParameters(assetModuleId, payload) {
    const http = new Http(variablesError);
    const mappedPayload = new CommutatorMaintenanceParameters.MapBack(payload);
    return http.post(urls.setParameters(assetModuleId), mappedPayload);
  },
  setDefaultParameters(assetModuleId) {
    const http = new Http({
      errorHandling: {
        ignore: true,
      },
    });
    return http.post(urls.setDefaultParameters(assetModuleId))
      .then(({ data }) => new DefaultCommutatorMaintenanceParameters(data));
  },
};
